import { useState, useEffect, useCallback } from 'react';

interface UseFetchDataReturn<T> {
  data: T | null;
  loading: boolean;
  /** True when the last fetch threw (network/5xx). data stays null — render an empty state, not a skeleton. */
  failed: boolean;
  refetch: () => Promise<void>; // Function to manually trigger data fetch
}

interface UseFetchDataOptions {
  enabled?: boolean;
}

const useFetchData = <T>(
  fetchService: (...args: any[]) => Promise<T>, // Accepts a function with any arguments
  args: any[], // Ensure arguments are passed as an array
  options: UseFetchDataOptions = {},
): UseFetchDataReturn<T> => {
  const { enabled = true } = options;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [failed, setFailed] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const result = await fetchService(...args); // Spread the array of arguments
      setData(result);
      setFailed(false);
    } catch {
      // A rejected fetch must not strand consumers on a permanent skeleton (data stays null):
      // they check `failed` and render their empty state instead.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [fetchService, enabled, ...args]); // Dependencies ensure stability of the function

  // Define refetch to call fetchData
  const refetch = useCallback(async (): Promise<void> => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchData(); // Automatically fetch data on mount or args change
  }, [fetchData, enabled]);

  return { data, loading, failed, refetch };
};

export default useFetchData;
